<template>
  <div class="contact-page">
    <Top />

    <section class="page-hero" aria-label="Contact header">
      <div class="container page-hero-inner">
        <h1 class="page-hero-title">CONTACT US</h1>
      </div>
    </section>

    <section class="contact" aria-label="Contact information">
      <div class="container contact-inner">
        <div v-if="loading" class="state-msg" role="status">Loading…</div>
        <div v-else-if="loadError" class="state-msg state-err">{{ loadError }}</div>
        <div v-else class="contact-grid">
          <div v-if="display.phone" class="contact-item">
            <svg viewBox="0 0 24 24" width="18" height="18" class="contact-icon" fill="#0c0b06">
              <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.07 21 3 13.93 3 5a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.46.57 3.58a1 1 0 0 1-.24 1.01l-2.2 2.2Z" />
            </svg>
            <div class="contact-item-body">
              <div class="contact-item-label">Call Us</div>
              <a class="contact-item-value contact-link" :href="'tel:' + telHref">{{ display.phone }}</a>
            </div>
          </div>
          <div v-if="display.email" class="contact-item">
            <svg viewBox="0 0 24 24" width="18" height="18" class="contact-icon" fill="#0c0b06">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
            <div class="contact-item-body">
              <div class="contact-item-label">Email Us</div>
              <a class="contact-item-value contact-link" :href="'mailto:' + display.email">{{ display.email }}</a>
            </div>
          </div>
          <div v-if="display.address" class="contact-item">
            <svg viewBox="0 0 24 24" width="18" height="18" class="contact-icon" fill="#0c0b06">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            <div class="contact-item-body">
              <div class="contact-item-label">Address</div>
              <div class="contact-item-value">{{ display.address }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="map-section" aria-label="Location map">
      <div class="googleMapBox">
        <div v-if="mapNotice" class="map-placeholder">{{ mapNotice }}</div>
        <div v-else ref="mapEl" class="googleMap-inner" />
      </div>
    </section>

    <section class="cta-band">
      <div class="container cta-inner">
        <div class="cta-left">
          <h2 class="cta-title">AESTHETICS PRACTITIONER?</h2>
          <p class="cta-sub">
            Grow your business with DOSE and access new patients, forms, diary, reminders<br />
            and no show/late cancellation protection.
          </p>
        </div>
        <div class="cta-right">
          <button class="cta-btn" type="button" @click="goAbout">LEARN MORE</button>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Footer from '../components/footer.vue'
import Top from '../components/top.vue'
import { fetchPublicContact } from '../api/contact'
import { GOOGLE_MAPS_API_KEY } from '../constants/googleMaps'

let mapsScriptPromise = null
function loadGoogleMapsScript(apiKey) {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'))
  if (window.google && window.google.maps) return Promise.resolve()
  if (mapsScriptPromise) return mapsScriptPromise
  mapsScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => {
      mapsScriptPromise = null
      reject(new Error('Failed to load Google Maps script.'))
    }
    document.head.appendChild(s)
  })
  return mapsScriptPromise
}

export default {
  name: 'ContactPage',
  components: {
    Footer,
    Top,
  },
  data() {
    return {
      loading: true,
      loadError: '',
      mapNotice: '',
      mapInstance: null,
      display: {
        phone: '',
        email: '',
        address: '',
        openingHours: '',
      },
    }
  },
  computed: {
    mapsApiKey() {
      return GOOGLE_MAPS_API_KEY
    },
    hasAnyInfo() {
      const d = this.display
      return !!(d.phone || d.email || d.address || d.openingHours)
    },
    telHref() {
      return String(this.display.phone || '').replace(/\s+/g, '')
    },
  },
  created() {
    fetchPublicContact()
      .then((data) => {
        this.display = {
          phone: (data.phone || '').trim(),
          email: (data.email || '').trim(),
          address: (data.address || '').trim(),
          openingHours: (data.openingHours || '').trim(),
        }
      })
      .catch((e) => {
        this.loadError = e.message || 'Failed to load contact information.'
      })
      .finally(() => {
        this.loading = false
      })
  },
  mounted() {
    if (!this.mapsApiKey) {
      this.mapNotice =
        'Set VUE_APP_GOOGLE_MAPS_API_KEY in your .env file to display the map.'
      return
    }
    this.$nextTick(() => {
      this.bootstrapGoogleMap()
    })
  },
  beforeDestroy() {
    this.mapInstance = null
  },
  methods: {
    bootstrapGoogleMap() {
      loadGoogleMapsScript(this.mapsApiKey)
        .then(() => {
          const paint = () => {
            this.$nextTick(() => {
              this.initGoogleMap()
            })
          }
          if (!this.loading) paint()
          else {
            const stop = this.$watch('loading', (v) => {
              if (!v) {
                stop()
                paint()
              }
            })
          }
        })
        .catch((e) => {
          this.mapNotice = e.message || 'Map could not be loaded.'
        })
    },
    initGoogleMap() {
      const el = this.$refs.mapEl
      if (!el || !window.google || !window.google.maps) return
      const g = window.google.maps
      const defaultCenter = { lat: 51.5074, lng: -0.1278 }
      const map = new g.Map(el, {
        center: defaultCenter,
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      })
      this.mapInstance = map
      const addr = (this.display.address || '').trim()
      if (!addr) return
      const geocoder = new g.Geocoder()
      geocoder.geocode({ address: addr }, (results, status) => {
        if (status !== 'OK' || !results || !results[0]) return
        const loc = results[0].geometry.location
        map.setCenter(loc)
        map.setZoom(15)
        new g.Marker({ map, position: loc })
      })
    },
    goAbout() {
      this.$router.push({ name: 'about' }).catch((err) => {
        if (err && err.name !== 'NavigationDuplicated') throw err
      })
    },
  },
}
</script>

<style scoped>
.contact-page {
  min-height: 100vh;
  background: #ffffff;
  color: #0c0b06;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-hero {
  background: #f4f3ef;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.page-hero-inner {
  height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-hero-title {
  margin: 0;
  font-size: 16px;
  letter-spacing: 0.16em;
  font-weight: 500;
  color: #0c0b06;
}

.contact-inner {
  padding: 60px 0 80px;
}

.state-msg {
  text-align: center;
  font-size: 13px;
  color: #0c0b06;
  padding: 24px 0;
}

.state-err {
  color: #c45656;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  max-width: 1000px;
  margin: 0 auto;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.contact-icon {
  color: #c4a77d;
  flex-shrink: 0;
  margin-top: 2px;
}

.contact-item-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.contact-item-label {
  color: #c4a77d;
  font-size: 16px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.contact-item-value {
  color: #0c0b06;
  font-size: 15px;
  line-height: 1.6;
}

.contact-link {
  color: inherit;
  text-decoration: none;
}

.contact-link:hover {
  text-decoration: underline;
}



.googleMapBox {
  max-width: 1060px;
  margin: 0 auto 60px auto;
  height: 420px;
  background: #e8e6e1;
  position: relative;
}

.googleMap-inner {
  width: 100%;
  height: 100%;
}

.map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
  font-size: 13px;
  color: #0c0b06;
  line-height: 1.6;
}

.cta-band {
  padding: 44px 0;
  background: #f4f3ef;
}

.cta-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
}

.cta-title {
  margin: 0;
  font-size: 18px;
  letter-spacing: 0.08em;
  font-weight: 600;
}

.cta-sub {
  margin: 10px 0 0;
  color: #0c0b06;
  font-size: 12px;
  line-height: 1.7;
}

.cta-btn {
  height: 36px;
  min-width: 150px;
  padding: 0 26px;
  border: none;
  border-radius: 0;
  background: #b08a4a;
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.14em;
  cursor: pointer;
}

.cta-btn:hover {
  filter: brightness(0.96);
}

@media (max-width: 720px) {
  .googleMapBox{
    margin:10px 12px;
  }
  .container {
    padding: 0 12px;
  }

  .page-hero-inner {
    height: 96px;
  }

  .contact-inner {
    padding: 40px 12px 60px 12px;
  }

  .contact-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .contact-item-value {
    font-size: 14px;
  }

  .cta-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .cta-btn {
    width: 100%;
    max-width: 220px;
  }

  .googleMapBox {
    height: 300px;
  }
}
</style>
